import React, { useState, useEffect } from "react";
import "./StudentEdit.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const GENDER_OPTIONS = ["Male", "Female"];
const ACTIVE_OPTIONS = [
  { value: "P", label: "Provisional" },
  { value: "V", label: "Verified" },
  { value: "R", label: "Rejected" },
];

export default function StudentEdit({ studentId, moduleId, onClose, onSaved }) {
  const [meta,    setMeta]    = useState(null);
  const [cities,  setCities]  = useState([]);
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [stuRes, metaRes] = await Promise.all([
          fetch(`${API_ROOT}/api/students/${studentId}?moduleId=${moduleId}`),
          fetch(`${API_ROOT}/api/students/form-meta?moduleId=${moduleId}`),
        ]);
        const stuData  = await stuRes.json();
        const metaData = await metaRes.json();
        if (!stuRes.ok)  throw new Error(stuData.message  || "Failed to load student.");
        if (!metaRes.ok) throw new Error(metaData.message || "Failed to load form data.");

        const stu = stuData.data;
        setMeta(metaData.data);
        setForm({
          NameTitleID:       stu.NameTitleID        || "",
          StudentName:       stu.StudentName        || "",
          FNameTitleID:      stu.FNameTitleID       || "",
          FatherName:        stu.FatherName         || "",
          Gender:            stu.Gender             || "",
          Country:           stu.Country            || "",
          Mobile:            stu.Mobile             || "",
          Email:             stu.Email              || "",
          DOB:               stu.DOB                || "",
          PUPIN:             stu.PUPIN              || "",
          VehicleNo:         stu.VehicleNo          || "",
          PermanentPIN:      stu.PermanentPIN       || "",
          PermanentAddress:  stu.PermanentAddress   || "",
          PermanentStateID:  stu.PermanentStateID   || "",
          PermanentCityID:   stu.PermanentCityID    || "",
          CourseID:          stu.CourseID           || "",
          DepartmentID:      stu.DepartmentID       || "",
          StickerNo:         stu.StickerNo          || "",
          Active:            stu.Active             || "P",
        });

        if (stu.PermanentStateID) {
          const cr = await fetch(
            `${API_ROOT}/api/students/cities?moduleId=${moduleId}&stateId=${stu.PermanentStateID}`
          );
          const cd = await cr.json();
          if (Array.isArray(cd)) setCities(cd);
        }
      } catch (err) {
        setError(err.message || "Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [studentId, moduleId]);

  const onChange = async (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === "PermanentStateID" && value) {
      try {
        const r = await fetch(
          `${API_ROOT}/api/students/cities?moduleId=${moduleId}&stateId=${value}`
        );
        const d = await r.json();
        if (Array.isArray(d)) {
          setCities(d);
          setForm(prev => ({ ...prev, PermanentCityID: "" }));
        }
      } catch {}
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    setSaving(true);
    try {
      const res = await fetch(
        `${API_ROOT}/api/students/${studentId}?moduleId=${moduleId}`,
        {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(form),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save.");
      setSuccess(data.message || "Saved successfully.");
      setTimeout(() => onSaved(), 1200);
    } catch (err) {
      setError(err.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="stu-edit-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Student</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body">
            {loading && <div className="text-center py-3">Loading...</div>}
            {error   && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {form && meta && !loading && (
              <form id="stu-edit-form" onSubmit={onSubmit}>
                <div className="row g-2">

                  <div className="col-md-3">
                    <label className="form-label">Title</label>
                    <select className="form-select" name="NameTitleID" value={form.NameTitleID} onChange={onChange}>
                      <option value="">-- Select --</option>
                      {meta.nameTitles.map(t => (
                        <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-9">
                    <label className="form-label">Student Name</label>
                    <input className="form-control" name="StudentName" value={form.StudentName} readOnly />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Father Title</label>
                    <select className="form-select" name="FNameTitleID" value={form.FNameTitleID} onChange={onChange}>
                      <option value="">-- Select --</option>
                      {meta.fatherTitles.map(t => (
                        <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-9">
                    <label className="form-label">Father Name</label>
                    <input className="form-control" name="FatherName" value={form.FatherName} onChange={onChange} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Gender</label>
                    <select className="form-select" name="Gender" value={form.Gender} onChange={onChange}>
                      <option value="">-- Select --</option>
                      {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Country</label>
                    <input className="form-control" name="Country" value={form.Country} readOnly />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Mobile</label>
                    <input className="form-control" name="Mobile" value={form.Mobile} readOnly />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input className="form-control" type="email" name="Email" value={form.Email} onChange={onChange} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Date of Birth</label>
                    <input className="form-control" type="date" name="DOB" value={form.DOB} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">PUPIN</label>
                    <input className="form-control" name="PUPIN" value={form.PUPIN} onChange={onChange} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Vehicle No</label>
                    <input className="form-control" name="VehicleNo" value={form.VehicleNo} onChange={onChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Sticker No</label>
                    <input className="form-control" name="StickerNo" value={form.StickerNo} onChange={onChange} />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Permanent PIN</label>
                    <input className="form-control" name="PermanentPIN" value={form.PermanentPIN} onChange={onChange} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Permanent Address</label>
                    <input className="form-control" name="PermanentAddress" value={form.PermanentAddress} onChange={onChange} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">State</label>
                    <select className="form-select" name="PermanentStateID" value={form.PermanentStateID} onChange={onChange}>
                      <option value="">-- Select State --</option>
                      {meta.states.map(s => (
                        <option key={s.StateID} value={s.StateID}>{s.State}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">City</label>
                    <select className="form-select" name="PermanentCityID" value={form.PermanentCityID} onChange={onChange}>
                      <option value="">-- Select City --</option>
                      {cities.map(c => (
                        <option key={c.CityID} value={c.CityID}>{c.City}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Course</label>
                    <select className="form-select" name="CourseID" value={form.CourseID} onChange={onChange}>
                      <option value="">-- Select Course --</option>
                      {meta.courses.map(c => (
                        <option key={c.CourseID} value={c.CourseID}>{c.Course}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Department</label>
                    <select className="form-select" name="DepartmentID" value={form.DepartmentID} onChange={onChange}>
                      <option value="">-- Select Department --</option>
                      {meta.departments.map(d => (
                        <option key={d.DepartmentID} value={d.DepartmentID}>{d.Department}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Active</label>
                    <select className="form-select" name="Active" value={form.Active} onChange={onChange}>
                      {ACTIVE_OPTIONS.map(a => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>

                </div>
              </form>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            <button
              type="submit"
              form="stu-edit-form"
              className="btn btn-primary"
              disabled={saving || loading || !form}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
